import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { Heading, Text } from 'tio-ui/components/typography';
import Button from 'tio-reimagined/components/button';
import {
  PhArrowRight,
  PhWarning,
  PhBriefcase,
  PhBank,
  PhHouse,
  PhRepeat,
} from 'ember-phosphor-icons';
import { eq } from 'tio-ui/utilities';
import type { PslfScenario } from 'tio-employee/services/career-pathways-api';

interface ScenarioCardSignature {
  Args: {
    scenario: PslfScenario;
    onExplore?: (scenarioType: string) => void;
  };
  Element: HTMLDivElement;
}

// Get icon based on scenario type
function getScenarioIcon(type: string): typeof PhBriefcase {
  switch (type) {
    case 'leave_public_service':
      return PhBriefcase;
    case 'refinance':
      return PhBank;
    case 'change_employer':
      return PhHouse;
    case 'change_plan':
      return PhRepeat;
    default:
      return PhBriefcase;
  }
}

// Map recommendation to severity
function getRecommendationSeverity(rec: PslfScenario['recommendation']) {
  switch (rec) {
    case 'do_not':
      return { color: 'red', label: 'Not Recommended' };
    case 'caution':
      return { color: 'amber', label: 'Use Caution' };
    case 'consider':
      return { color: 'blue', label: 'Consider Carefully' };
    case 'safe':
      return { color: 'emerald', label: 'Safe Option' };
    default:
      return { color: 'gray', label: 'Unknown' };
  }
}

export default class PslfScenarioCard extends Component<ScenarioCardSignature> {
  get Icon() {
    return getScenarioIcon(this.args.scenario.type);
  }

  get severity() {
    return getRecommendationSeverity(this.args.scenario.recommendation);
  }

  get isWarning() {
    return (
      this.args.scenario.recommendation === 'do_not' ||
      this.args.scenario.recommendation === 'caution'
    );
  }

  get formattedForgivenessLoss() {
    const amount = this.args.scenario.impact.forgivenessLost;
    if (amount === 0) return '$0';
    return `-$${amount.toLocaleString()}`;
  }

  get formattedAdditionalCost() {
    const cost = this.args.scenario.impact.additionalCost;
    if (cost === 0) return '$0';
    return `+$${cost.toLocaleString()}`;
  }

  handleExplore = () => {
    this.args.onExplore?.(this.args.scenario.type);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="bg-white rounded-xl p-5 border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all"
      ...attributes
    >
      {{! Header }}
      <div class="flex items-start justify-between gap-3 mb-4">
        <div class="flex items-center gap-3 min-w-0">
          <div
            class="p-2 rounded-lg flex-shrink-0
              {{if
                this.isWarning
                'bg-red-100 dark:bg-red-500/20'
                'bg-blue-100 dark:bg-blue-500/20'
              }}"
          >
            <this.Icon
              @size={{24}}
              class="{{if
                  this.isWarning
                  'text-red-600 dark:text-red-400'
                  'text-blue-600 dark:text-blue-400'
                }}"
            />
          </div>
          <Heading @level={{3}} class="text-gray-900 font-semibold text-base">
            {{@scenario.title}}
          </Heading>
        </div>
        <div
          class="px-2 py-1 text-xs font-bold rounded-full whitespace-nowrap flex-shrink-0
            {{if
              (eq this.severity.color 'red')
              'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400'
            }}
            {{if
              (eq this.severity.color 'amber')
              'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400'
            }}
            {{if
              (eq this.severity.color 'blue')
              'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400'
            }}
            {{if
              (eq this.severity.color 'emerald')
              'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
            }}"
        >
          {{this.severity.label}}
        </div>
      </div>

      {{! Description }}
      <Text class="text-gray-600 text-sm mb-4">
        {{@scenario.description}}
      </Text>

      {{! Impact Analysis }}
      <div class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 mb-4">
        <Text class="text-gray-500 text-xs uppercase tracking-wide mb-3">
          Impact Analysis
        </Text>

        <div class="grid grid-cols-3 gap-3">
          {{! Forgiveness Lost }}
          <div class="min-w-0">
            <Text class="text-gray-500 text-xs mb-1">Forgiveness Lost</Text>
            <Text class="font-bold text-red-600 dark:text-red-400 text-sm">
              {{this.formattedForgivenessLoss}}
            </Text>
          </div>

          {{! Additional Cost }}
          <div class="min-w-0">
            <Text class="text-gray-500 text-xs mb-1">Extra Cost</Text>
            <Text class="font-bold text-amber-600 dark:text-amber-400 text-sm">
              {{this.formattedAdditionalCost}}
            </Text>
          </div>

          {{! Time Impact }}
          <div class="min-w-0">
            <Text class="text-gray-500 text-xs mb-1">Timeline</Text>
            <Text class="font-bold text-gray-700 text-sm">
              {{@scenario.impact.timeImpact}}
            </Text>
          </div>
        </div>
      </div>

      {{! Warning message }}
      {{#if this.isWarning}}
        <div
          class="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg mb-4"
        >
          <PhWarning @size={{18}} class="text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <Text class="text-red-600 dark:text-red-300 text-sm">
            {{@scenario.message}}
          </Text>
        </div>
      {{/if}}

      {{! Details }}
      {{#if @scenario.details.length}}
        <div class="mb-4">
          <Text class="text-gray-500 text-xs uppercase tracking-wide mb-2">Key Factors</Text>
          <ul class="space-y-1">
            {{#each @scenario.details as |detail|}}
              <li class="text-gray-600 text-sm flex items-center gap-2">
                <span
                  class="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full flex-shrink-0"
                ></span>
                {{detail}}
              </li>
            {{/each}}
          </ul>
        </div>
      {{/if}}

      {{! Explore Button }}
      {{#if @onExplore}}
        <Button
          @intent="primary"
          @appearance="outlined"
          class="w-full justify-center"
          {{on "click" this.handleExplore}}
        >
          <span>Explore this scenario</span>
          <PhArrowRight @size={{16}} />
        </Button>
      {{/if}}
    </div>
  </template>
}
